import { useState, useEffect } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Clock,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  FileText,
  GraduationCap,
  Plus,
  Edit,
  Trash2,
  ChevronRight,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const actionIcons = {
  create: <Plus className="w-4 h-4 text-green-500" />,
  update: <Edit className="w-4 h-4 text-amber-500" />,
  delete: <Trash2 className="w-4 h-4 text-red-500" />
};

const actionLabels = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete'
};

const entityIcons = {
  university: <GraduationCap className="w-5 h-5" />,
  blog_post: <FileText className="w-5 h-5" />,
  event: <Calendar className="w-5 h-5" />
};

export const PendingApprovals = () => {
  const { token, canApprove, logout } = useCMSAuth();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [reviewAction, setReviewAction] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  useEffect(() => {
    fetchDrafts();
  }, [activeTab]);

  const fetchDrafts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/drafts?status=${activeTab}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      if (res.ok) {
        const data = await res.json();
        setDrafts(data);
      }
    } catch (e) {
      toast.error('Failed to fetch drafts');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedDraft || !reviewAction) return;
    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/cms/drafts/${selectedDraft.id}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: reviewAction,
          notes: reviewNotes || null
        })
      });

      if (res.ok) {
        toast.success(`Draft ${reviewAction === 'approve' ? 'approved' : 'rejected'} successfully`);
        setReviewDialogOpen(false);
        setSelectedDraft(null);
        setReviewNotes('');
        fetchDrafts();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to process review');
      }
    } catch (e) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const openReviewDialog = (draft, action) => {
    setSelectedDraft(draft);
    setReviewAction(action);
    setReviewDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="pending-approvals">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-ux-charcoal">Content Approvals</h1>
          <p className="text-ux-charcoal/60">
            Review and approve content changes submitted by editors
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="w-4 h-4" />
              Pending
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-2">
              <CheckCircle className="w-4 h-4" />
              Approved
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-2">
              <XCircle className="w-4 h-4" />
              Rejected
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-ux-cyan border-t-transparent rounded-full" />
              </div>
            ) : drafts.length > 0 ? (
              <div className="space-y-4">
                {drafts.map((draft) => (
                  <Card key={draft.id} data-testid={`draft-${draft.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        {/* Left: Draft Info */}
                        <div className="flex items-start gap-4 flex-1">
                          {/* Entity Icon */}
                          <div className="w-10 h-10 rounded-lg bg-ux-cyan/10 flex items-center justify-center text-ux-cyan">
                            {entityIcons[draft.entity_type] || <FileText className="w-5 h-5" />}
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-ux-charcoal truncate">
                                {draft.entity_name}
                              </h3>
                              <Badge 
                                variant="outline" 
                                className={
                                  draft.action === 'create' ? 'border-green-300 text-green-600' :
                                  draft.action === 'update' ? 'border-amber-300 text-amber-600' :
                                  'border-red-300 text-red-600'
                                }
                              >
                                {actionIcons[draft.action]}
                                <span className="ml-1">{actionLabels[draft.action]}</span>
                              </Badge>
                            </div>

                            <p className="text-sm text-ux-charcoal/60 mb-2">
                              {draft.change_summary}
                            </p>

                            <div className="flex items-center gap-4 text-xs text-ux-charcoal/50">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {draft.submitted_by_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDate(draft.submitted_at)}
                              </span>
                            </div>

                            {/* Review info for approved/rejected */}
                            {draft.status !== 'pending' && draft.reviewed_by_name && (
                              <div className="mt-2 pt-2 border-t text-xs">
                                <span className={draft.status === 'approved' ? 'text-green-600' : 'text-red-600'}>
                                  {draft.status === 'approved' ? 'Approved' : 'Rejected'} by {draft.reviewed_by_name}
                                </span>
                                {draft.review_notes && (
                                  <p className="text-ux-charcoal/60 mt-1">"{draft.review_notes}"</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right: Actions */}
                        {activeTab === 'pending' && canApprove() && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => openReviewDialog(draft, 'reject')}
                              data-testid={`reject-${draft.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-500 hover:bg-green-600"
                              onClick={() => openReviewDialog(draft, 'approve')}
                              data-testid={`approve-${draft.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                          </div>
                        )}
                      </div>

                      {/* Expandable Changes Preview */}
                      {draft.draft_value && Object.keys(draft.draft_value).length > 0 && (
                        <details className="mt-4 border-t pt-4">
                          <summary className="cursor-pointer text-sm text-ux-cyan flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            View Changes
                            <ChevronRight className="w-4 h-4" />
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                            <pre className="text-xs text-ux-charcoal/80">
                              {JSON.stringify(draft.draft_value, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-ux-charcoal/30" />
                  <p className="text-ux-charcoal/60">
                    {activeTab === 'pending' 
                      ? 'No pending content awaiting approval'
                      : `No ${activeTab} drafts`
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Review Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {reviewAction === 'approve' ? 'Approve' : 'Reject'} Change
              </DialogTitle>
              <DialogDescription>
                {reviewAction === 'approve' 
                  ? 'This will apply the changes to the live content.'
                  : 'This will reject the changes and notify the editor.'
                }
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-ux-charcoal">{selectedDraft?.entity_name}</p>
                <p className="text-xs text-ux-charcoal/60">{selectedDraft?.change_summary}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-ux-charcoal">
                  {reviewAction === 'approve' ? 'Notes (optional)' : 'Reason for rejection'}
                </label>
                <Textarea
                  placeholder={reviewAction === 'approve' 
                    ? 'Add any notes about this approval...'
                    : 'Explain why this change was rejected...'
                  }
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="mt-1"
                  required={reviewAction === 'reject'}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleReview}
                disabled={submitting || (reviewAction === 'reject' && !reviewNotes.trim())}
                className={reviewAction === 'approve' 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-red-500 hover:bg-red-600'
                }
              >
                {submitting 
                  ? 'Processing...' 
                  : reviewAction === 'approve' ? 'Approve Changes' : 'Reject Changes'
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default PendingApprovals;
